import React, { useContext, useEffect, useState } from "react";
import { popupcontext } from "../../../../context/Popupscontext";
import OfflineSetupControlHeader from "./OfflineSetupControlHeader";
import OfflineSetupControlLocation from "./OfflineSetupControlLocation";
import OfflineSetupControlModule from "./OfflineSetupControlModule";
import OfflineSetupControlFormFooter from "./OfflineSetupControlFormFooter";
import * as Yup from "yup";
import trans, { isRTL } from "../../../../utils";
import { useFormik } from "formik";
import {
	useAddConfigurationMutation,
	useEditConfigurationMutation,
	useGetSingleConfigurationQuery,
} from "../../../../Redux/service/Configuration/Configuration";
import { toast } from "react-toastify";
import Loader from "../../../Helpers/Loader/Loader";
import { useNavigate, useParams } from "react-router-dom";

export default function OfflineSetupControlForm({
	setFloorplan,
	setRooms,
	uniqueId,
	setUniqueId,
	setError,
}) {
	const params = useParams();
	const [submitAttempt, setSubmitAttempt] = useState(false);
	const { setReloadData } = useContext(popupcontext);
	const navigate = useNavigate();
	const param = useParams();
	const { data, isError, error } = useGetSingleConfigurationQuery(param?.id, {
		skip: !param?.id,
		refetchOnMountOrArgChange: true,
	});

	const [addConfiguration, { isLoading: addLoading }] =
		useAddConfigurationMutation();
	const [editConfiguration, { isLoading: editLoading }] =
		useEditConfigurationMutation();
	let initialValues = {
		name: null,
		projectId: null,
		buildingId: null,
		levelId: null,
		modules: [],
	};
	let validSchema = Yup.object({
		name: Yup.string()
			.required(trans("Name is required"))
			.min(3, trans("Name must be at least 3 characters"))
			.test(
				"not-empty-or-spaces",
				trans("Name cannot be just whitespace"),
				(value) => !!value && value.trim().length > 0,
			)
			.matches(
				/^[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FFA-Za-z0-9\s\-]+$/,
				trans("Name cannot contain special characters"),
			)
			.test(
				"no-double-dash",
				trans("Name cannot contain double dashes"),
				(value) => {
					if (typeof value !== "string") return false;
					return !/-{2,}/.test(value);
				},
			)
			.test(
				"no-leading-space",
				trans("Name cannot start with a space"),
				(value) => {
					if (typeof value !== "string") return false;
					return !/^\s/.test(value);
				},
			)
			.test(
				"no-multiple-spaces",
				trans("Name cannot contain multiple spaces in a row"),
				(value) => {
					if (typeof value !== "string") return false;
					return !/ {2,}/.test(value);
				},
			),
		projectId: Yup.string().required(trans("Project is required")),
		buildingId: Yup.string().required(trans("Building is required")),
		levelId: Yup.string().required(trans("Level is required")),
		modules: Yup.array()
			.of(
				Yup.object().shape({
					module: Yup.string().required(),
					discipline: Yup.string().when("module", {
						is: (val) => val === "IR" || val === "SNAG",
						then: (schema) => schema.required("Discipline is required"),
					}),
					formId: Yup.number().when("module", {
						is: "IR",
						then: (schema) => schema.required("Form is required"),
					}),
					sector: Yup.mixed().when("module", {
						is: "SNAG",
						then: (schema) => schema.required("Sector is required"),
					}),
				}),
			)
			.min(1, trans("At least one module is required")),
	});
	let addConfig = useFormik({
		initialValues,
		validationSchema: validSchema,
		onSubmit: async (values) => {
			try {
				console.log(values);
				if (!params?.id) {
					await addConfiguration(values).unwrap();
					toast.success(trans("Configuration Is Added Successfully"), {
						autoClose: 800,
						rtl: localStorage.getItem("lang") === "ar" ? true : false,
					});
					setReloadData(true);
					setSubmitAttempt(false);
					navigate("/setup/offline-setup");
				} else {
					await editConfiguration({
						id: params?.id,
						val: values,
					}).unwrap();
					toast.success(trans("Configuration Is Edited Successfully"), {
						autoClose: 800,
						rtl: localStorage.getItem("lang") === "ar" ? true : false,
					});
					setReloadData(true);
					setSubmitAttempt(false);
					navigate("/setup/offline-setup");
				}
			} catch (error) {
				toast.error(`${error?.data?.message}`, {
					autoClose: 800,
					rtl: localStorage.getItem("lang") === "ar" ? true : false,
				});
			}
		},
	});

	const handleSubmit = (e) => {
		e.preventDefault();
		setSubmitAttempt(true);
		addConfig.handleSubmit(e);
	};

	useEffect(() => {
		if (data) {
			addConfig.setFieldValue("name", data?.data?.name ?? null);
			addConfig.setFieldValue("projectId", data?.data?.projectId ?? null);
			addConfig.setFieldValue("buildingId", data?.data?.buildingId ?? null);
			addConfig.setFieldValue("levelId", data?.data?.levelId ?? null);
			addConfig.setFieldValue("modules", data?.data?.modules ?? null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data]);
	useEffect(() => {
		if (!isError) return;

		if (error?.status === 500) {
			toast.error(trans("Server error, please try again later"), {
				autoClose: 800,
				rtl: localStorage.getItem("lang") === "ar" ? true : false,
			});
		} else if (error?.status === 401) {
			toast.error(trans("Unauthorized request"), {
				autoClose: 800,
				rtl: localStorage.getItem("lang") === "ar" ? true : false,
			});
		} else {
			setError(error);
			toast.error(trans(error?.data?.message) || trans("Request failed"), {
				autoClose: 800,
				rtl: localStorage.getItem("lang") === "ar" ? true : false,
			});
		}
	}, [isError, error]);
	// console.log("AddConfig", addConfig);

	return (
		<div className="col-lg-4">
			<form
				onSubmit={handleSubmit}
				style={{
					width: "100%",
					backgroundColor: "white",
					height: "calc(100vh - 64px)",
					display: "flex",
					flexDirection: "column",
					borderLeft: !isRTL && "1px solid #D2D5DC",
					borderRight: isRTL && "1px solid #D2D5DC",
				}}
			>
				{!addLoading || !editLoading ? (
					<>
						<div
							style={{
								flex: 1,
								overflowY: "auto",
								// paddingBlock: "16px",
							}}
						>
							<OfflineSetupControlHeader />
							<OfflineSetupControlLocation
								addConfig={addConfig}
								setFloorplan={setFloorplan}
								setRooms={setRooms}
								uniqueId={uniqueId}
								setUniqueId={setUniqueId}
							/>
							<OfflineSetupControlModule
								addConfig={addConfig}
								submitAttempt={submitAttempt}
							/>
						</div>
						<OfflineSetupControlFormFooter
							addConfig={addConfig}
							submitAttempt={submitAttempt}
						/>{" "}
					</>
				) : (
					<div className="loader-container">
						<Loader />
					</div>
				)}
			</form>{" "}
		</div>
	);
}
